// FLAGS: authorization, health-data
// The session table shared by AllSessionsView and MySessionsView: its `show*` props are the whole
// client-side gate on reassign, cancel, reschedule, status change and join-link copy.
import {
  Box,
  Grid,
  Icon,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ChangeSessionStatusDialog from "@src/components/ChangeSessionStatusDialog/ChangeSessionStatusDialog";
import NothingFound from "@src/components/NothingFound";
import PageTitle from "@openup/shared/components/PageTitle/PageTitle";
import SwapPsychologistDialog from "@src/components/SwapPsychologistDialog";
import { isEmptyQueryResult } from "@src/queries/utils";
import {
  AmsterdamTimezone,
  pageSize,
  STATUS,
  STATUS_SUBJECT_FOR_CHANGE,
} from "@src/constants";
import {
  formatFirstUpperCase,
  formatStandardDateOnly,
  formatTimeOnly,
} from "@src/utils/formatter";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import {
  WithSnackbarProps,
  withSnackbar,
} from "@src/components/SnackBarComponent";
import {
  HighlightOffOutlined,
  InfoOutlined,
  MoreVert as MoreVertIcon,
  PlayCircleOutline,
  SocialDistance,
  SyncAltOutlined,
} from "@mui/icons-material";
import { IconSessionLanguage } from "@src/components/Icons/IconSessionLanguage";
import {
  ResponsePsychologistConsultInformationResponse,
  ResponsePsychologistGetPsychologistConsultsResponse,
} from "@src/api/v1/data-contracts";
import Button from "@openup/shared/components/Button/Button";
import { PrivateSpaceRow } from "@src/views/MySessions/privateSpaceRow";

// One row of either sessions feed. The second half was previously the `...Paginated` wrapper, which
// made the type demand `data` and `pagination` on every row, so nothing could ever satisfy it; the
// intended type is the row it wraps. It is intersected as `Partial` because the two feeds are not
// interchangeable: All Sessions supplies `employee`, the expert's own consults feed does not, which
// is why `showPsychologistName` reads it optionally.
type SessionListItem = ResponsePsychologistGetPsychologistConsultsResponse &
  Partial<ResponsePsychologistConsultInformationResponse>;

// This table lists two unrelated things. A 1:1 consult has a client, a language, a relationship
// stage and a full set of expert actions; a hosted private space has an employer, a title, a
// format and nothing but a way in. They are kept as a discriminated union rather than one widened
// row type so a space can never be read as a consult with missing fields, which is what would let
// the client-dossier navigation or a reschedule route fire against a session that has no client.
export type SessionsViewRow =
  | { kind: "consult"; consult: SessionListItem }
  | { kind: "privateSpace"; space: PrivateSpaceRow };

export interface SessionsViewProps extends WithSnackbarProps {
  titleText: string;
  page: number;
  onPageChange: (page: number) => void;
  columns: { id: string; minWidth: number; width?: number }[];
  isFetching: boolean;
  /** Consult-only callers pass this; supply either `consults` or `rows`, not both. */
  consults?: SessionListItem[];
  /**
   * Ordered rows for the current page, when the caller lists more than consults. Takes precedence
   * over `consults`; callers that only ever show 1:1 sessions keep passing `consults` alone.
   */
  rows?: SessionsViewRow[];
  /**
   * Supplied by callers that list private spaces. The start-link request lives with the caller, not
   * here: this table is rendered in tests and by All Sessions without a query client, so owning a
   * mutation hook would make every consumer depend on one to show 1:1 sessions.
   */
  privateSpaceJoin?: {
    onJoin: (sessionId: string) => void;
    isJoining: boolean;
    error: string | null;
  };
  total: number;
  hasNextPage: boolean;
  showReassign?: boolean;
  showPsychologistName?: boolean;
  showJoinSession?: boolean;
  showRescheduleSession?: boolean;
  showCancelSession?: boolean;
  showCancelSessionInMenu?: boolean;
  showSessionDetails?: boolean;
  showChangeSessionStatus?: boolean;
  showCopyJoinLink: boolean;
  showReferralToolkitIcon: boolean;
  showSessionLanguage: boolean;
  showSessionStage?: boolean;
}
const SessionsView: FC<SessionsViewProps> = ({
  titleText,
  page,
  onPageChange,
  columns,
  isFetching,
  consults = [],
  rows,
  privateSpaceJoin,
  total,
  hasNextPage,
  showReassign = false,
  showPsychologistName = false,
  showJoinSession = true,
  showRescheduleSession = true,
  showCancelSession = true,
  showCancelSessionInMenu = false,
  showSessionDetails = true,
  showChangeSessionStatus = false,
  showCopyJoinLink = false,
  showReferralToolkitIcon = false,
  showSessionLanguage = false,
  showSessionStage = false,
  snackbarShowMessage,
}) => {
  const buttonIconClassName =
    "w-8 h-8 p-0 flex justify-center items-center mr-2 p-1 rounded bg-blue-500 text-white hover:opacity-70";
  const buttonIconDisabledClassName =
    " bg-slate-300 hover:bg-slate-300 hover:opacity-100 cursor-not-allowed";

  const { t } = useTranslation();
  const navigate = useNavigate();

  const showActionsMenu =
    showRescheduleSession ||
    (showCancelSessionInMenu && showCancelSession) ||
    showCopyJoinLink;
  const [isSwapPsychologistOpen, setIsSwapPsychologistOpen] = useState(false);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [selectedConsult, setSelectedConsult] =
    useState<SessionListItem | null>(null);
  const [openTableMenus, setOpenTableMenus] = useState<{
    [id: string]: HTMLElement | null;
  }>({});
  // A type predicate rather than a plain boolean: "internally scheduled" *means* the consult has
  // an expert room URL, so saying so in the signature lets every guarded branch treat
  // `psychologistUrl` as present instead of asserting it.
  const isInternallyScheduled = (
    consult: SessionListItem,
  ): consult is SessionListItem & { psychologistUrl: string } =>
    !!consult.psychologistUrl;

  const displayRows: SessionsViewRow[] =
    rows ?? consults.map((consult) => ({ kind: "consult", consult }));

  // A space with no join handler wired up is shown disabled rather than hidden: the row is still
  // the honest record that the session exists.
  const canJoinPrivateSpace = (space: PrivateSpaceRow) =>
    !!privateSpaceJoin && space.canStart && !privateSpaceJoin.isJoining;

  // Deliberately not the consult row with fields blanked out. A space has no client dossier to
  // open, no language, no relationship stage and no reschedule or cancel route, so the only
  // action offered is the way in. The conditional cells mirror the consult row's own conditionals
  // so both rows keep the same cell count as the `columns` header.
  const renderPrivateSpaceRow = (space: PrivateSpaceRow) => (
    <TableRow
      hover
      tabIndex={-1}
      key={space.sessionId}
      className="opacity-70"
      data-testid="private-space-row"
    >
      <TableCell className="py-2 px-1 sub1">
        <div className="flex flex-col">
          <span>{formatStandardDateOnly(space.startsAt, space.timeZone)}</span>
          <span>{formatTimeOnly(space.startsAt, space.timeZone)}</span>
        </div>
      </TableCell>
      {showReferralToolkitIcon && <TableCell className="py-2 sub1" />}
      <TableCell className="py-2 px-1 sub1">{space.employerName}</TableCell>
      <TableCell className="py-2 sub1">{space.title}</TableCell>
      {showSessionLanguage && (
        <TableCell className="justify-center">
          {/* Only when the event declared a source language. IconSessionLanguage renders an
              error glyph when the flag lookup fails, so an absent language must not reach it. */}
          {space.languageCode && (
            <IconSessionLanguage
              tooltip={t(`SessionLanguage.${space.languageCode}`)}
              language={space.languageCode}
              className="h-7"
            />
          )}
        </TableCell>
      )}
      {showSessionStage && (
        <TableCell className="py-2 px-1 sub1">
          {t(space.typeLabelKey)}
        </TableCell>
      )}
      {showPsychologistName && <TableCell className="py-2 px-1 sub1" />}
      <TableCell className="py-2 px-1 sub1">
        {t(`PrivateSpaces.SessionStatus.${space.status}`)}
      </TableCell>
      <TableCell className="py-2 px-1 sub1">
        <div className="flex items-center">
          {/* No data-testid here: Tooltip forwards unknown props to its child, where the icon's
              own testid would override it and silently leave this one unmatched. */}
          <Tooltip title={t("Common.JoinSession")} placement="top">
            <PlayCircleOutline
              className={
                buttonIconClassName +
                (canJoinPrivateSpace(space) ? "" : buttonIconDisabledClassName)
              }
              data-testid={`start-session-${space.sessionId}`}
              onClick={() => {
                if (canJoinPrivateSpace(space)) {
                  privateSpaceJoin?.onJoin(space.sessionId);
                }
              }}
            />
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );

  const handleActionsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number,
  ) => {
    event.stopPropagation();
    const tableMenus = { ...openTableMenus };
    tableMenus[id] = event.currentTarget;
    setOpenTableMenus(tableMenus);
  };
  const handleActionsClose = (id: number) => {
    const tableMenus = { ...openTableMenus };
    tableMenus[id] = null;
    setOpenTableMenus(tableMenus);
  };
  const handleClickSwapSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    setSelectedConsult(consult);
    setIsSwapPsychologistOpen(true);
  };
  const handleClickChangeSessionStatus = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    // The truthiness check is what narrows the optional status; an absent one could never be in
    // the list, so this rejects exactly what it rejected before.
    if (consult.status && STATUS_SUBJECT_FOR_CHANGE.includes(consult.status)) {
      setSelectedConsult(consult);
      setChangeStatusOpen(true);
    }
  };

  const handleClickStartSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    if (isInternallyScheduled(consult)) {
      window.open(consult.psychologistUrl, "_blank", "noopener");
    }
  };

  const handleClickRescheduleSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    handleActionsClose(consult.id);
    if (isInternallyScheduled(consult)) {
      navigate(`/my-sessions/consult/${consult.id}/time-picker`);
    }
  };

  const handleClickCancelSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    handleActionsClose(consult.id);
    if (isInternallyScheduled(consult)) {
      navigate(`/my-sessions/consult/${consult.id}/cancel-session`);
    }
  };

  // FLAGS: authorization
  // Opening a row navigates to the client's full dossier page, which applies its own access rules
  // independently of `showSessionDetails` — narrowing this prop hides the affordance, not the page.
  const handleClickSessionRow = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    if (showSessionDetails) {
      navigate(`/clients/${consult.user?.id}`);
    }
  };

  // FLAGS: authorization
  // Puts the client's guest join URL on the clipboard — a bearer link into a therapy session, so it
  // leaves the app's control once copied. `guestUrl` is non-null asserted: when the field is absent the
  // clipboard receives the string "undefined" and the success snackbar still shows.
  const handleClickCopyJoinLink = async (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    handleActionsClose(consult.id);
    await navigator.clipboard.writeText(consult.guestUrl!);
    snackbarShowMessage(t("SessionsView.LinkCopiedClipboard"), "success");
  };
  return (
    <>
      <PageTitle applicationName={t("Navigation.Title")} title={titleText} />
      {isSwapPsychologistOpen && (
        <SwapPsychologistDialog
          setIsOpen={setIsSwapPsychologistOpen}
          consult={selectedConsult}
        />
      )}
      {selectedConsult && (
        <ChangeSessionStatusDialog
          open={changeStatusOpen}
          setOpen={setChangeStatusOpen}
          consult={selectedConsult}
        />
      )}
      <Grid container spacing={4}>
        {isEmptyQueryResult(displayRows) ? (
          <NothingFound translationKey="Sessions.List.NotFound" />
        ) : (
          <Grid item xs={12}>
            <Paper className="px-3 py-4 rounded-md">
              <Box display="flex" alignItems="center">
                <Icon className="mr-2">fact_check_outlined</Icon>
                <h5 className="h5">{titleText}</h5>
                <TablePagination
                  component="div"
                  className="ml-auto -mr-2 mb-4"
                  count={total}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={[10]}
                  page={page}
                  onPageChange={(_, newPage) => {
                    onPageChange(newPage);
                  }}
                  slotProps={{
                    actions: {
                      nextButton: { disabled: isFetching || !hasNextPage },
                      previousButton: { disabled: isFetching || page === 0 },
                    },
                  }}
                />
              </Box>
              {privateSpaceJoin?.error && (
                <p className="mb-3 text-sm text-red-600" role="alert">
                  {privateSpaceJoin.error}
                </p>
              )}
              {!isFetching && (
                <TableContainer>
                  <Table
                    stickyHeader
                    aria-label="sticky table"
                    data-testid="all-sessions-table"
                    className="table-fixed"
                  >
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            style={{
                              minWidth: column.minWidth,
                              width: column.width,
                            }}
                            className="pl-1 py-4 sub1"
                          >
                            {t(column.id)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayRows.map((row) => {
                        if (row.kind === "privateSpace") {
                          return renderPrivateSpaceRow(row.space);
                        }

                        const { consult } = row;
                        return (
                          <TableRow
                            hover
                            tabIndex={-1}
                            key={consult.id}
                            className="opacity-70"
                          >
                            <TableCell
                              className="py-2 px-1 sub1 hover:text-blue-600 cursor-pointer"
                              onClick={(event) => {
                                handleClickSessionRow(event, consult);
                              }}
                            >
                              <div className="flex flex-col">
                                <span>
                                  {formatStandardDateOnly(
                                    consult.startedAt,
                                    AmsterdamTimezone,
                                  )}
                                </span>
                                <span>
                                  {formatTimeOnly(
                                    consult.startedAt,
                                    AmsterdamTimezone,
                                  )}
                                </span>
                              </div>
                            </TableCell>
                            {showReferralToolkitIcon && (
                              <TableCell className="py-2 sub1 hover:text-blue-600">
                                {consult.employerOfClientHasReferralToolkit && (
                                  <Tooltip
                                    title={t(
                                      "MySessions.EmployerReferralToolkitInfo.Tooltip",
                                    )}
                                    placement="top"
                                    data-testid="employer-referral-toolkit-info-column"
                                  >
                                    <InfoOutlined data-testid="employer-referral-toolkit-info-icon" />
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                            <TableCell
                              className="py-2 px-1 sub1 cursor-pointer"
                              onClick={(event) => {
                                handleClickSessionRow(event, consult);
                              }}
                            >
                              {`${consult.user?.firstName} ${
                                consult.user?.lastName ?? ""
                              } `}
                            </TableCell>

                            <TableCell className="py-2 sub1 hover:text-blue-600">
                              {consult.consultType
                                ? formatFirstUpperCase(consult.consultType)
                                : ""}
                            </TableCell>
                            {showSessionLanguage && (
                              <TableCell className="justify-center">
                                <IconSessionLanguage
                                  tooltip={t(
                                    `SessionLanguage.${consult.consultLanguage}`,
                                  )}
                                  language={consult.consultLanguage}
                                  className="h-7"
                                />
                              </TableCell>
                            )}
                            {showSessionStage && (
                              <TableCell className="py-2 px-1 sub1 hover:text-blue-600">
                                {consult.consultStage}
                              </TableCell>
                            )}
                            {showPsychologistName && (
                              <TableCell
                                className="py-2 px-1 sub1 hover:text-blue-600"
                                data-cy="psychologist-column"
                              >
                                {`${consult.employee?.firstName} ${consult.employee?.lastName ?? ""}`}
                              </TableCell>
                            )}
                            <TableCell className="py-2 px-1 sub1">
                              <div className="flex">
                                <div className="flex flex-col align-center gap-1">
                                  <span className="mr-2 hover:text-blue-600">
                                    {t(
                                      `Consults.Status.${formatFirstUpperCase(
                                        consult.status,
                                      )}`,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="py-2 px-1 sub1">
                              <div className="flex items-center">
                                {consult.status!.toLowerCase() ===
                                  STATUS.SCHEDULED.toLowerCase() && (
                                  <>
                                    {showReassign && (
                                      <Tooltip
                                        title={t("ReassignPsychologist.Title")}
                                        placement="top"
                                        data-testid="reassign-session-button"
                                      >
                                        <SocialDistance
                                          className={buttonIconClassName}
                                          onClick={(event) => {
                                            handleClickSwapSession(
                                              event,
                                              consult,
                                            );
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    {!showCancelSessionInMenu &&
                                      showCancelSession && (
                                        <Tooltip
                                          title={t("Common.CancelSession")}
                                          placement="top"
                                          data-testid="cancel-session-button"
                                        >
                                          <HighlightOffOutlined
                                            className={
                                              buttonIconClassName +
                                              (isInternallyScheduled(consult)
                                                ? ""
                                                : buttonIconDisabledClassName)
                                            }
                                            onClick={(event) => {
                                              handleClickCancelSession(
                                                event,
                                                consult,
                                              );
                                            }}
                                          />
                                        </Tooltip>
                                      )}

                                    {showJoinSession && (
                                      <Tooltip
                                        title={t("Common.JoinSession")}
                                        placement="top"
                                        data-testid="join-session-button"
                                      >
                                        <PlayCircleOutline
                                          className={
                                            buttonIconClassName +
                                            (isInternallyScheduled(consult)
                                              ? ""
                                              : buttonIconDisabledClassName)
                                          }
                                          onClick={(event) => {
                                            handleClickStartSession(
                                              event,
                                              consult,
                                            );
                                          }}
                                        />
                                      </Tooltip>
                                    )}

                                    {showActionsMenu && (
                                      <Button
                                        data-testid={`actions-${consult.id}`}
                                        onClick={(
                                          e: React.MouseEvent<HTMLButtonElement>,
                                        ) => handleActionsClick(e, consult.id)}
                                        variant="tertiary"
                                        className="mr-2 rounded-sm [&&]:px-0 [&&]:py-0 h-8 w-8"
                                      >
                                        <MoreVertIcon className="text-2xl text-indigo-800" />
                                      </Button>
                                    )}

                                    {showActionsMenu && (
                                      <Menu
                                        key={consult.id}
                                        anchorEl={openTableMenus[consult.id]}
                                        open={Boolean(
                                          openTableMenus[consult.id],
                                        )}
                                        onClose={() =>
                                          handleActionsClose(consult.id)
                                        }
                                        MenuListProps={{
                                          "aria-labelledby": "basic-button",
                                        }}
                                        slotProps={{
                                          paper: {
                                            className:
                                              "mt-1 rounded border border-solid border-slate-300",
                                          },
                                        }}
                                      >
                                        {showRescheduleSession && (
                                          <MenuItem
                                            onClick={(event) => {
                                              handleClickRescheduleSession(
                                                event,
                                                consult,
                                              );
                                            }}
                                            disabled={
                                              !isInternallyScheduled(consult)
                                            }
                                            data-testid={`reschedule-session-${consult.id}`}
                                            className="hover:text-blue-500 hover:bg-slate-100"
                                          >
                                            {t("Common.RescheduleSession")}
                                          </MenuItem>
                                        )}
                                        {showCancelSessionInMenu &&
                                          showCancelSession && (
                                            <MenuItem
                                              onClick={(event) => {
                                                handleClickCancelSession(
                                                  event,
                                                  consult,
                                                );
                                              }}
                                              disabled={
                                                !isInternallyScheduled(consult)
                                              }
                                              className="hover:text-blue-500 hover:bg-slate-100"
                                              data-testid={`cancel-session-${consult.id}`}
                                            >
                                              {t("Common.CancelSession")}
                                            </MenuItem>
                                          )}

                                        {showCopyJoinLink && (
                                          <MenuItem
                                            onClick={(event) =>
                                              handleClickCopyJoinLink(
                                                event,
                                                consult,
                                              )
                                            }
                                            className="hover:text-blue-500 hover:bg-slate-100"
                                            data-testid={`copy-link-${consult.id}`}
                                          >
                                            {t("SessionsView.CopyJoinLink")}
                                          </MenuItem>
                                        )}
                                      </Menu>
                                    )}
                                  </>
                                )}
                                {showChangeSessionStatus &&
                                  STATUS_SUBJECT_FOR_CHANGE.includes(
                                    consult.status!,
                                  ) && (
                                    <Tooltip
                                      title={t("AllSessions.ChangeStatus")}
                                      placement="top"
                                    >
                                      <SyncAltOutlined
                                        data-testid="change-status-button"
                                        className={buttonIconClassName}
                                        onClick={(event) => {
                                          handleClickChangeSessionStatus(
                                            event,
                                            consult,
                                          );
                                        }}
                                      >
                                        sync_alt_outlined
                                      </SyncAltOutlined>
                                    </Tooltip>
                                  )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {isFetching && (
                <div className="flex w-full justify-center">
                  <Spinner />
                </div>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default withSnackbar(SessionsView);
